// The API's records as the web reads them. Every call goes through api() from @ghezaa/shell (queries.ts, mutations.ts).
import type { Page } from "@ghezaa/shell";

export type { Page };

export type MarketCan = { own: boolean; team: boolean; manage: boolean; erp: boolean; settings: boolean; reports: boolean; share: boolean };

/** A market Sales serves and what the person may do in it. `company`: the BC company it is linked to. */
export type Market = { code: string; name: string; currency: string; timeZone: string; company: string | null; can: MarketCan };

export type MarketsAnswer = { markets: Market[]; canSetUp: boolean; canSeeLog: boolean };

export type AvailableMarket = { code: string; name: string; country: string };

export type SalespersonOption = { code: string; name: string };

/** The person in one market: their salesperson codes, whether they see every book, and whose books they see. */
export type MeInMarket = { market: string; codes: string[]; team: boolean; salespeople: SalespersonOption[] };

export type CustomerRow = {
  no: string;
  name: string;
  searchName: string;
  city: string;
  salespersonCode: string;
  salespersonName: string;
  outlets: number;
  balance: number;
  overdue: number;
  creditLimit: number;
  /** BC's Blocked: "" or Ship, Invoice, All. */
  blocked: string;
  /** Where it stands (SL1): its last invoice, its revenue over the last 365 days, whether it has gone silent. */
  lastInvoiceOn: string | null;
  revenue365: number;
  silent: boolean;
};

export type OutletRow = { code: string; name: string; city: string; phone: string; salespersonCode: string };

export type CustomerDetail = {
  no: string;
  name: string;
  searchName: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  salesperson: { code: string; name: string; person: string | null } | null;
  paymentTerms: string;
  postingGroup: string;
  currency: string;
  blocked: string;
  creditLimit: number;
  balance: number;
  overdue: number;
  salesToDate: number;
  readAt: string;
  outlets: OutletRow[];
};

export type PersonRef = { id: string; name: string; email: string };

/** A BC salesperson code and who it is. `inBc` false: linked here, gone from BC's last read. */
export type SalespersonRow = {
  code: string;
  name: string;
  email: string;
  jobTitle: string;
  blocked: boolean;
  inBc: boolean;
  customers: number;
  person: PersonRef | null;
  manager: PersonRef | null;
  notAPerson: boolean;
  suggested: PersonRef | null;
};

export type BcRead = { at: string | null; failedAt: string | null; problem: string | null; error: string | null };

export type BcLink = { companyId: string; companyName: string; environment: string; linkedAt: string; linkedBy: string; read: BcRead };

/** The sales read: how it last went, how many value entries Sales holds, up to which entry, over which days. */
export type BcSales = { read: BcRead; entries: number; lastEntryNo: number; from: string | null; to: string | null };

export type BcStatus = {
  configured: boolean;
  environment: string;
  link: BcLink | null;
  counts: { salespeople: number; customers: number; outlets: number; items: number; categories: number; dimensionValues: number };
  sales: BcSales;
};

export type BcCompany = { id: string; name: string; displayName: string };

/** A period of days, both ends included (yyyy-MM-dd). */
export type Period = { from: string; to: string };

/** The measures over a set of sales entries (docs/sales-plan.md §5). */
export type Totals = {
  revenue: number;
  cost: number;
  grossProfit: number;
  margin: number | null;
  invoices: number;
  creditMemos: number;
  lines: number;
  customers: number;
  linesPerInvoice: number | null;
  averageInvoice: number | null;
};

export type SilentRow = { no: string; name: string; lastInvoiceOn: string; daysSince: number; usualGapDays: number; revenue365: number; overdue: number };

export type OverdueRow = { no: string; name: string; overdue: number; balance: number; creditLimit: number };

/** A person's day: `everyone` when they see every book and hold no code, so the figures are the market's. */
export type TodayAnswer = {
  linked: boolean;
  everyone: boolean;
  codes: string[];
  month: Period;
  before: Period;
  now: Totals;
  then: Totals;
  overdue: number;
  overdueCustomers: number;
  silentCustomers: number;
  silent: SilentRow[];
  overdueList: OverdueRow[];
  targets: TargetCard[];
  readAt: string | null;
};

/** One code's month; code "" is the sales with no salesperson, and the total's code is "" too. */
export type TeamRow = {
  code: string;
  name: string;
  person: string | null;
  now: Totals;
  revenueBefore: number;
  newCustomers: number;
  bookCustomers: number;
  silent: number;
  overdue: number;
  /** Its targets' overall attainment this month (SL2); null without targets. */
  attainment: number | null;
};

export type TeamAnswer = { month: string; soFar: boolean; now: Period; before: Period; rows: TeamRow[]; total: TeamRow; readAt: string | null };

export type MonthRow = { month: string; totals: Totals };

export type ItemRow = { no: string; name: string; quantity: number; revenue: number; revenueBefore: number };

export type CustomerFigures = {
  months: MonthRow[];
  recent: Period;
  earlier: Period;
  items: ItemRow[];
  firstInvoiceOn: string | null;
  lastInvoiceOn: string | null;
  usualGapDays: number | null;
  silent: boolean;
  revenue365: number;
  grossProfit365: number;
};

export type GapCustomer = { no: string; name: string; revenue: number; salespersonCode: string };

export type GapCode = { code: string; name: string; customers: number };

export type DataGaps = {
  year: Period;
  revenue: number;
  revenueNoSalesperson: number;
  documentsNoSalesperson: number;
  revenueNoBusinessUnit: number;
  documentsNoBusinessUnit: number;
  noBusinessUnit: GapCustomer[];
  customersNoSalesperson: GapCustomer[];
  customersOnNoOne: GapCustomer[];
  codesNotLinked: GapCode[];
};

export type TargetMeasure = "Revenue" | "GrossProfit" | "Margin" | "Customers" | "NewCustomers" | "LinesPerInvoice";

/** One target's month: the figure set, its weight, the figure so far, the share reached, where it should be by today. */
export type TargetLine = { measure: TargetMeasure; target: number; weight: number; actual: number; attainment: number | null; pace: number | null };

export type TargetCard = { code: string; lines: TargetLine[]; overall: number | null };

export type TargetRow = { code: string; name: string; person: string | null; lines: TargetLine[]; overall: number | null; usual: Partial<Record<TargetMeasure, number>> };

/** A month's targets; `editable` for this month and those to come. */
export type TargetsAnswer = { month: string; editable: boolean; previous: string; previousSet: boolean; rows: TargetRow[] };

export type ReportDimension =
  | "Salesperson" | "Manager" | "Customer" | "City" | "Item" | "ProductCategory" | "BusinessUnit" | "Location" | "DocumentType" | "Document"
  | "Day" | "Week" | "Month" | "Quarter" | "Year";

export type ReportMeasure =
  | "Revenue" | "Cost" | "GrossProfit" | "Margin" | "Quantity" | "Invoices" | "CreditMemos" | "Credited" | "CreditNoteRate"
  | "Lines" | "LinesPerInvoice" | "AverageInvoice" | "Customers" | "NewCustomers" | "Share" | "Attainment";

export type ReportPeriod =
  | "ThisWeek" | "LastWeek" | "ThisMonth" | "LastMonth" | "ThisQuarter" | "LastQuarter" | "ThisYear" | "LastYear"
  | "Last7Days" | "Last30Days" | "Last90Days" | "Last12Months" | "Custom";

export type ReportCompare = "None" | "PreviousPeriod" | "LastYear";

export type ReportAcross = "None" | "Week" | "Month" | "Quarter";

/** Only rows whose dimension is one of the values; "" is the rows with none (no salesperson, no business unit). */
export type ReportFilter = { dimension: ReportDimension; values: string[] };

/** A report's definition, the same on screen, saved and exported (Features/Reports/ReportSpec.cs). */
export type ReportSpec = {
  rows: ReportDimension;
  then: ReportDimension | null;
  measures: ReportMeasure[];
  period: ReportPeriod;
  from: string | null;
  to: string | null;
  compare: ReportCompare;
  across: ReportAcross;
  filters: ReportFilter[];
};

export type MeasureFormat = "money" | "percent" | "count" | "decimal" | "quantity";

export type MeasureInfo = { key: ReportMeasure; label: string; format: MeasureFormat; definition: string };

export type DimensionInfo = { key: ReportDimension; label: string; definition: string; showsCode: boolean; filterable: boolean; time: boolean };

export type Choice<T> = { key: T; label: string };

export type ReportStarter = { key: string; name: string; description: string; spec: ReportSpec };

export type ReportCatalogue = {
  measures: MeasureInfo[];
  dimensions: DimensionInfo[];
  periods: Choice<ReportPeriod>[];
  compares: Choice<ReportCompare>[];
  across: Choice<ReportAcross>[];
  starters: ReportStarter[];
};

/** `dimension` (the rows' names), `measure`, `before` (the period compared with), `change`, `bucket` (a week or month across), `total`. */
export type ReportColumn = {
  key: string;
  label: string;
  kind: "dimension" | "measure" | "before" | "change" | "bucket" | "total";
  format: MeasureFormat | "text" | "points";
  measure: ReportMeasure | null;
};

export type ReportRow = { keys: string[]; labels: string[]; values: (number | null)[] };

export type ReportValue = { code: string; name: string };

/** A report's answer. `everyone`: every book; otherwise the person's own codes. `total` is over every entry, not the rows' sum. */
export type ReportTable = {
  spec: ReportSpec;
  now: Period;
  before: Period | null;
  everyone: boolean;
  codes: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  total: (number | null)[];
  rowCount: number;
  truncated: boolean;
  filters: { dimension: ReportDimension; values: ReportValue[] }[];
  figuresFrom: string | null;
  readAt: string | null;
};

/** A saved report; `spec` null when this release no longer reads what was saved. */
export type ReportView = { id: string; name: string; ownerName: string; mine: boolean; shared: boolean; spec: ReportSpec | null; updatedAt: string };

export type ViewsAnswer = { mine: ReportView[]; shared: ReportView[] };

export type LogRow = { id: string; at: string; who: string; action: string; market: string; summary: string };

/** A query string from the values that are set: `?q=bakery&sort=-overdue`. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "" && value !== false) search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** The market's path in the API: `/api/markets/EG`. */
export const inMarket = (market: string) => `/api/markets/${encodeURIComponent(market)}`;
